import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { VenueTypeCode } from '../constants/venueTypes';
import { VenueType } from '../constants/venueTypes';
import { VenueCard } from '../../venues/components/VenueCard';
import { useFeaturedVenues } from '../hooks/useFeaturedVenues';
import { useCity } from '../../../contexts/CityContext';
import { fadeUp, reducedMotionVariants } from '../../../theme/animations';
import { venueTypeColors } from '../../../theme/venueTypeColors';
import { SectionTitle } from './SectionHeader';

const CarouselContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  background: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 500,
  fontSize: '1rem',
  textTransform: 'none',
  minWidth: 120,
  [theme.breakpoints.down('sm')]: {
    minWidth: 80,
    fontSize: '0.875rem',
  },
}));

const VenuesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  padding: '80px 0',
});

type TabValue = 'all' | VenueTypeCode;

export function FeaturedVenuesCarousel() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<TabValue>('all');
  const { selectedCity } = useCity();
  const shouldReduceMotion = useReducedMotion();
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  const { data, isLoading, error } = useFeaturedVenues({
    venueType: activeTab === 'all' ? undefined : activeTab,
    limit: 8,
    cityId: selectedCity?.id,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const handleVenueClick = (
    venueId: string,
    stateSlug?: string,
    citySlug?: string,
    venueSlug?: string
  ) => {
    if (stateSlug && citySlug && venueSlug) {
      navigate(`/${stateSlug}/${citySlug}/${venueSlug}`);
    } else {
      navigate(`/venues/${venueId}`);
    }
  };

  const getTabColor = (tabValue: TabValue): string => {
    if (tabValue === 'all') return theme.palette.secondary.main;
    return venueTypeColors[tabValue].primary;
  };

  return (
    <CarouselContainer sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          transition: 'background 0.3s ease',
          background:
            activeTab === VenueType.GAMING
              ? `radial-gradient(ellipse at center, ${theme.palette.venue.gaming}08 0%, transparent 70%)`
              : activeTab === VenueType.SNOOKER
                ? `radial-gradient(ellipse at center, ${theme.palette.venue.snooker}08 0%, transparent 70%)`
                : activeTab === VenueType.KIDS_PLAYAREA
                  ? `radial-gradient(ellipse at center, ${theme.palette.venue.kids}08 0%, transparent 70%)`
                  : 'none',
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={itemVariants}
        >
          <SectionTitle sx={{ mb: 6 }}>Featured Venues</SectionTitle>

          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: getTabColor(activeTab),
              },
            }}
          >
            <StyledTab
              label="All Venues"
              value="all"
              sx={{
                color: activeTab === 'all' ? getTabColor('all') : 'text.secondary',
                '&.Mui-selected': { color: getTabColor('all') },
              }}
            />
            <StyledTab
              label="Gaming"
              value={VenueType.GAMING}
              sx={{
                color:
                  activeTab === VenueType.GAMING ? getTabColor(VenueType.GAMING) : 'text.secondary',
                '&.Mui-selected': { color: getTabColor(VenueType.GAMING) },
              }}
            />
            <StyledTab
              label="Snooker"
              value={VenueType.SNOOKER}
              sx={{
                color:
                  activeTab === VenueType.SNOOKER
                    ? getTabColor(VenueType.SNOOKER)
                    : 'text.secondary',
                '&.Mui-selected': { color: getTabColor(VenueType.SNOOKER) },
              }}
            />
            <StyledTab
              label="Kids Play"
              value={VenueType.KIDS_PLAYAREA}
              sx={{
                color:
                  activeTab === VenueType.KIDS_PLAYAREA
                    ? getTabColor(VenueType.KIDS_PLAYAREA)
                    : 'text.secondary',
                '&.Mui-selected': { color: getTabColor(VenueType.KIDS_PLAYAREA) },
              }}
            />
          </StyledTabs>

          {isLoading && (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              Failed to load featured venues. Please try again later.
            </Alert>
          )}

          {data && data.data.length > 0 && (
            <VenuesGrid>
              {data.data.map((venue) => (
                <VenueCard
                  key={venue.id}
                  venue={venue}
                  onClick={() =>
                    handleVenueClick(
                      venue.id,
                      venue.state?.slug ?? undefined,
                      venue.city?.slug ?? undefined,
                      venue.slug
                    )
                  }
                  href={
                    venue.state?.slug && venue.city?.slug
                      ? `/${venue.state.slug}/${venue.city.slug}/${venue.slug}`
                      : undefined
                  }
                />
              ))}
            </VenuesGrid>
          )}

          {data && data.data.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                borderRadius: 3,
                border: '1px dashed',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Typography
                variant="h6"
                sx={(t) => ({
                  fontFamily: t.typography.fontFamilyMono,
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1,
                })}
              >
                {selectedCity?.name
                  ? `We're launching in ${selectedCity.name} soon`
                  : 'New venues are being added every week'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                  maxWidth: 400,
                  mx: 'auto',
                }}
              >
                {selectedCity?.name
                  ? `Know a great venue in ${selectedCity.name}? Let them know about us.`
                  : 'We handpick every venue on our platform to ensure quality.'}
              </Typography>
              <Button
                variant="outlined"
                href="/list-your-venue"
                sx={(t) => ({
                  fontFamily: t.typography.fontFamilyMono,
                  textTransform: 'none',
                  borderRadius: 2,
                })}
              >
                Suggest a Venue
              </Button>
            </Box>
          )}
        </motion.div>
      </Container>
    </CarouselContainer>
  );
}
